import { createContext, useContext, ReactNode } from 'react';
import { UseMutationResult } from '@tanstack/react-query';
import { getPolicyName } from '../../utils/fns';
import { OverAllPolicyType, ServiceCollection } from '../../utils/types';

interface StatusMutationVariables {
  policyId: string;
  currentStatus: number;
}

interface ReorderMutationVariables {
  policyId: string;
  newPriority: number;
  oldIndex: number;
  targetIndex: number;
}

// Compound key combining policyName, overAllPolicyType, collectionType, and orgId
export type PolicyMutationKey = string;

// Helper function to create a mutation key from policy data
export function createPolicyMutationKey(
  policyType: number,
  policySubType: number | undefined,
  overAllPolicyType: OverAllPolicyType,
  collectionType: ServiceCollection,
  orgId: string
): PolicyMutationKey {
  const policyName = getPolicyName(policyType, policySubType);
  return `${policyName}:${overAllPolicyType}:${collectionType}:${orgId}`;
}

interface PolicyMutationContextValue {
  mutatingPolicyTypes: Set<PolicyMutationKey>;
  reorderingPolicyTypes: Set<PolicyMutationKey>;
  statusMutation: UseMutationResult<any, any, StatusMutationVariables, any>;
  reorderMutation: UseMutationResult<any, any, ReorderMutationVariables, any>;
}

const PolicyMutationContext = createContext<
  PolicyMutationContextValue | undefined
>(undefined);

export const PolicyMutationProvider = ({
  children,
  value,
}: {
  children: ReactNode;
  value: PolicyMutationContextValue;
}) => {
  return (
    <PolicyMutationContext.Provider value={value}>
      {children}
    </PolicyMutationContext.Provider>
  );
};

export const usePolicyMutation = () => {
  const context = useContext(PolicyMutationContext);
  if (!context) {
    throw new Error(
      'usePolicyMutation must be used within PolicyMutationProvider'
    );
  }
  return context;
};
